import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How long does ceramic coating last?",
      answer: "Longevity depends on the selected product, preparation, storage, mileage, and maintenance. Before application, we explain the expected term, curing requirements, maintenance plan, and any product-specific warranty conditions."
    },
    {
      question: "Do you offer mobile detailing?",
      answer: "Yes. Mobile service is available in Simi Valley, Ventura County, and select Los Angeles communities. Some paint correction and ceramic coating work may be scheduled in the appointment-only garage setting when lighting, weather, or cure conditions make that the better choice."
    },
    {
      question: "How long does a detail take?",
      answer: "Maintenance services may take approximately 1–1.5 hours, deeper detailing may take 2–4 hours, paint correction can take 4–8 hours or longer, and ceramic coating may require 1–2 days. Vehicle size, condition, and selected service determine the final timeframe."
    },
    {
      question: "What is included in interior detailing?",
      answer: "The scope is matched to the condition of the interior. Services may include detailed vacuuming, surface cleaning, steam treatment, carpet and upholstery shampooing, leather-safe cleaning and conditioning, crevice cleaning, and odor treatment when selected."
    },
    {
      question: "Can paint correction remove every scratch?",
      answer: "No. Paint correction improves defects in the clear coat, including many swirl marks, haze, oxidation, and light scratches. Deep scratches that have passed through the clear coat may require touch-up paint or body repair. We set realistic expectations after inspection."
    },
    {
      question: "Do I still need to wash a ceramic-coated vehicle?",
      answer: "Yes. A coating can make correct washing and drying easier, but it does not make the vehicle maintenance-free or scratch-proof. Regular paint-safe washing helps preserve gloss and coating performance."
    },
    {
      question: "Do you work on all vehicle types?",
      answer: "Detailing Disciples works on daily drivers, luxury vehicles, sports cars, trucks, and SUVs. Oversized vehicles, specialty finishes, and unusual conditions are quoted individually."
    },
    {
      question: "What is your cancellation policy?",
      answer: "Please contact us as soon as possible if you need to reschedule. Any deposit, cancellation, or late-arrival terms will be shown clearly before the appointment is confirmed."
    },
    {
      question: "Do you offer warranties or guarantees?",
      answer: "Product warranty terms vary by coating package and require the specified preparation and maintenance. We explain any applicable coverage in writing before service. If you have a concern with the workmanship, contact us promptly so the vehicle can be inspected."
    },
    {
      question: "What payment methods do you accept?",
      answer: "Accepted payment methods and any applicable deposit requirements are displayed during booking or on the final invoice."
    }
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Auto Detailing FAQ | Detailing Disciples"
        description="Answers about ceramic coating, paint correction, mobile detailing, service times, maintenance, pricing and appointments."
        canonical="https://thedetailingdisciples.com/faq"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <HelpCircle className="w-16 h-16 text-[#B8860B] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Auto Detailing Questions, <span className="text-[#B8860B]">Answered</span>
            </h1>
            <p className="text-xl text-gray-300">
              Common questions about our services, process, and policies
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={`faq-${index}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-[#B8860B]/50 transition-all"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-[#B8860B] focus:ring-offset-2 focus:ring-offset-[#111216]"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-white font-bold text-lg pr-4">{faq.question}</h3>
                  {openIndex === index ? (
                    <ChevronUp className="w-6 h-6 text-[#B8860B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-[#B8860B] flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div
                    id={`faq-answer-${index}`}
                    className="px-8 pb-6 text-gray-300 leading-relaxed"
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Call, text, or request a quote and we'll answer your specific questions
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+17473207174" className="w-full sm:w-auto">
              <button className="w-full sm:w-auto bg-[#B8860B] hover:bg-[#C8960B] text-[#111216] font-bold px-8 py-4 rounded-lg transition-colors">
                Call (747) 320-7174
              </button>
            </a>
            <a href="/contact" className="w-full sm:w-auto">
              <button className="w-full sm:w-auto border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold px-8 py-4 rounded-lg transition-colors">
                Request a Quote
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
